/**
 * The same strategy on other markets — BE `POST /hypotheses/{id}/markets-test` (a job).
 * The finished table is also kept on the backtest as `last_markets`.
 */
import { enqueueAndPollStrategyRdJob } from "./strategyRdApi";
import { STRATEGY_RD_API_BASE } from "../types/strategyRd";
import type { StrategyRdJobProgress } from "../utils/strategyRdJobs";

export interface StrategyRdMarketRow {
  symbol: string;
  original: boolean;
  status: "ok" | "no_trades" | "error" | string;
  message?: string | null;
  trades: number | null;
  winRate: number | null;
  profitFactor: number | null;
  returnPct: number | null;
  maxDrawdownPct: number | null;
  expectancyR: number | null;
  sumR: number | null;
  unseenVerdict: string | null;
  costsProfitFactor: number | null;
  costsReturnPct: number | null;
}

export interface StrategyRdMarketsResult {
  markets: StrategyRdMarketRow[];
  verdict: "carries" | "some_markets" | "one_market" | "no_market" | "too_few_trades" | "too_few_markets" | string | null;
  tested: number;
  judged: number;
  held: string[];
  profitable: string[];
  appliedAt: string | null;
  savedAt: string | null;
}

const rec = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

export function parseStrategyRdMarketsResult(raw: unknown): StrategyRdMarketsResult | null {
  const r = rec(raw);
  if (!Array.isArray(r.markets)) return null;
  const summary = rec(r.summary);
  return {
    markets: (r.markets as unknown[]).map((m) => {
      const x = rec(m);
      const st = rec(x.stats);
      const unseen = rec(x.unseen);
      const costs = rec(x.with_costs);
      return {
        symbol: str(x.symbol) ?? "?",
        original: x.original === true,
        status: str(x.status) ?? "error",
        message: str(x.message),
        trades: num(st.closed_count),
        winRate: num(st.win_rate),
        profitFactor: num(st.profit_factor) ?? num(st.profit_factor_r),
        returnPct: num(st.return_on_starting_equity_pct),
        maxDrawdownPct: num(st.max_drawdown_pct),
        expectancyR: num(st.expectancy_r),
        sumR: num(st.sum_r),
        unseenVerdict: str(unseen.verdict),
        costsProfitFactor: num(costs.profit_factor) ?? num(costs.profit_factor_r),
        costsReturnPct: num(costs.return_on_starting_equity_pct),
      };
    }),
    verdict: str(summary.verdict),
    tested: num(summary.tested) ?? 0,
    judged: num(summary.judged) ?? 0,
    held: Array.isArray(summary.markets) ? (summary.markets as unknown[]).map(String) : [],
    profitable: Array.isArray(summary.profitable) ? (summary.profitable as unknown[]).map(String) : [],
    appliedAt: str(r.applied_at),
    savedAt: str(r.saved_at),
  };
}

export async function runStrategyRdMarketsTest(
  hypothesisId: string,
  symbols: string[],
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdMarketsResult> {
  const payload = await enqueueAndPollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/hypotheses/${encodeURIComponent(hypothesisId.trim())}/markets-test`,
    { symbols },
    { signal, maxWaitMs: 300_000, onProgress, stopOn: ["ready", "failed"], fallbackErrorMessage: "Could not test other markets." }
  );
  const parsed = parseStrategyRdMarketsResult(payload);
  if (!parsed) throw new Error("Could not read the markets test.");
  return parsed;
}
